import api from "../../../lib/axios";
import type { BaseResponse } from "../../../lib/api-contract";
import type {
  OutboxNotificationExport,
  OutboxNotificationDetail,
  OutboxNotificationFilters,
  OutboxNotificationList,
  OutboxNotificationPagination,
} from "../types/outboxNotifications.types";

const OUTBOX_NOTIFICATIONS_PATH = "/v1/portal/outbox-notifications";

type OutboxNotificationListParams = OutboxNotificationFilters & {
  page: number;
  pageSize: number;
};

type RawOutboxNotificationList = {
  summary?: OutboxNotificationList["summary"];
  items?: OutboxNotificationList["items"];
  pagination?: OutboxNotificationPagination;
};

const unwrap = <T>(response: BaseResponse<T>): T => (response.data ?? {}) as T;

const toQueryParams = <T extends Record<string, unknown>>(filters: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(filters).filter(([, value]) => value !== undefined && value !== ""),
  ) as Partial<T>;

const getDownloadFilename = (contentDisposition: string | undefined): string =>
  contentDisposition?.split("filename=")[1]?.replace(/"/g, "") ?? "outbox-notifications.csv";

export async function getOutboxNotifications(
  params: OutboxNotificationListParams,
  signal?: AbortSignal,
): Promise<OutboxNotificationList> {
  const response = await api.get<BaseResponse<RawOutboxNotificationList>>(OUTBOX_NOTIFICATIONS_PATH, {
    params: toQueryParams(params),
    signal,
  });
  const data = unwrap(response.data);

  return {
    summary: data.summary ?? [],
    items: data.items ?? [],
    pagination: data.pagination ?? {
      page: params.page,
      pageSize: params.pageSize,
      totalCount: 0,
      totalPages: 0,
    },
  };
}

export async function exportOutboxNotifications(
  filters: OutboxNotificationFilters,
  signal?: AbortSignal,
): Promise<OutboxNotificationExport> {
  const response = await api.get(`${OUTBOX_NOTIFICATIONS_PATH}/export`, {
    params: toQueryParams(filters),
    responseType: "blob",
    signal,
  });

  return {
    data: response.data as BlobPart,
    contentType: (response.headers?.["content-type"] as string | undefined) ?? "text/csv",
    filename: getDownloadFilename(response.headers?.["content-disposition"] as string | undefined),
  };
}

export async function getOutboxNotificationDetail(
  id: string,
  signal?: AbortSignal,
): Promise<OutboxNotificationDetail> {
  const response = await api.get<BaseResponse<OutboxNotificationDetail>>(
    `${OUTBOX_NOTIFICATIONS_PATH}/${id}`,
    { signal },
  );

  return unwrap(response.data);
}
